import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MonthlyDemand } from '../../services/partDemandTypes';
import { formatDate } from '../quoteToCash/format';
import { TooltipRow } from '../quoteToCash/LineChart';

import { DEMAND_COLORS, formatQty } from './format';

const HEIGHT = 280;
const MARGIN = { top: 16, right: 16, bottom: 28, left: 56 };
const TICKS = 4;

/** The smallest of 1, 2, 2.5 or 5 times a power of ten at or above `value` */
function niceStep(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  return ([1, 2, 2.5, 5, 10].find((m) => m * power >= value) ?? 10) * power;
}

interface DemandChartProps {
  monthly: MonthlyDemand[];
  uom: string;
}

/**
 * Monthly demand as stacked bars, customer demand under internal consumption, with returns hanging below the axis
 * and the 3-month rolling average of total demand as a line. The current month is drawn lighter: it is to date.
 * A crosshair (pointer or arrow keys) shows the month's figures; a table carries them for screen readers.
 */
export const DemandChart: React.FC<DemandChartProps> = ({ monthly, uom }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(280, entry.contentRect.width))
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const qty = (value: number) => formatQty(value, uom, lang);
  const count = monthly.length;
  const plotWidth = width - MARGIN.left - MARGIN.right;
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const top = Math.max(
    0,
    ...monthly.map((m) => m.customerDemand + m.internalConsumption),
    ...monthly.map((m) => m.rollingAverage3M ?? 0)
  );
  const bottom = Math.max(0, ...monthly.map((m) => m.returns));
  const step = niceStep((top + bottom) / TICKS);
  const above = Math.max(1, Math.ceil(top / step));
  const below = Math.ceil(bottom / step);
  const range = (above + below) * step;
  const y = (value: number) =>
    MARGIN.top + ((above * step - value) / range) * plotHeight;
  const band = plotWidth / Math.max(1, count);
  const barWidth = Math.max(4, Math.min(40, band * 0.6));
  const center = (i: number) => MARGIN.left + band * (i + 0.5);
  const labelEvery = Math.max(1, Math.ceil(count / Math.floor(plotWidth / 48)));
  const label = (month: string) => formatDate(`${month}-01`, lang, 'monthYear');
  const ticks = Array.from(
    { length: above + below + 1 },
    (_, i) => (above - i) * step
  );

  const series = [
    {
      key: 'customer',
      label: t('partDemand.chart.customer'),
      color: DEMAND_COLORS.customer,
    },
    {
      key: 'internal',
      label: t('partDemand.chart.internal'),
      color: DEMAND_COLORS.internal,
    },
    {
      key: 'returns',
      label: t('partDemand.chart.returns'),
      color: DEMAND_COLORS.returns,
    },
  ];
  const average = monthly
    .map((m, i) =>
      m.rollingAverage3M === null ? null : ([i, m.rollingAverage3M] as const)
    )
    .filter((p): p is readonly [number, number] => p !== null);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const delta = event.key === 'ArrowRight' ? 1 : -1;
    setHover((h) =>
      Math.min(count - 1, Math.max(0, (h ?? (delta > 0 ? -1 : count)) + delta))
    );
  };

  return (
    <div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2 text-sm text-gray-600">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ background: s.color }}
              aria-hidden="true"
            />
            {s.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-2">
          <svg width="20" height="4" aria-hidden="true">
            <line
              x1="0"
              y1="2"
              x2="20"
              y2="2"
              stroke={DEMAND_COLORS.average}
              strokeWidth="2"
            />
          </svg>
          {t('partDemand.chart.average')}
        </span>
      </div>
      <div ref={containerRef} className="relative w-full">
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={t('partDemand.chart.title')}
          tabIndex={0}
          className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          onPointerMove={(e) => {
            const left = e.currentTarget.getBoundingClientRect().left;
            const i = Math.floor((e.clientX - left - MARGIN.left) / band);
            setHover(Math.min(count - 1, Math.max(0, i)));
          }}
          onPointerLeave={() => setHover(null)}
          onKeyDown={onKeyDown}
          onBlur={() => setHover(null)}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke={tick === 0 ? '#9ca3af' : '#f0f0ee'}
              />
              <text
                x={MARGIN.left - 8}
                y={y(tick)}
                textAnchor="end"
                dominantBaseline="middle"
                className="fill-gray-500 text-[11px]"
              >
                {qty(tick)}
              </text>
            </g>
          ))}
          {hover !== null && (
            <rect
              x={MARGIN.left + band * hover}
              y={MARGIN.top}
              width={band}
              height={plotHeight}
              fill="#f3f4f6"
            />
          )}
          {monthly.map((m, i) => {
            const x = center(i) - barWidth / 2;
            const opacity = m.partial ? 0.45 : 1;
            return (
              <g key={m.month} opacity={opacity}>
                {m.customerDemand > 0 && (
                  <rect
                    x={x}
                    y={y(m.customerDemand)}
                    width={barWidth}
                    height={y(0) - y(m.customerDemand)}
                    fill={DEMAND_COLORS.customer}
                  />
                )}
                {m.internalConsumption > 0 && (
                  <rect
                    x={x}
                    y={y(Math.max(0, m.customerDemand) + m.internalConsumption)}
                    width={barWidth}
                    height={y(0) - y(m.internalConsumption)}
                    fill={DEMAND_COLORS.internal}
                  />
                )}
                {m.returns > 0 && (
                  <rect
                    x={x}
                    y={y(0)}
                    width={barWidth}
                    height={y(-m.returns) - y(0)}
                    fill={DEMAND_COLORS.returns}
                  />
                )}
                {i % labelEvery === 0 && (
                  <text
                    x={center(i)}
                    y={HEIGHT - 8}
                    textAnchor="middle"
                    className="fill-gray-500 text-[11px]"
                  >
                    {label(m.month)}
                  </text>
                )}
              </g>
            );
          })}
          {average.length > 1 && (
            <path
              d={average
                .map(
                  ([i, v], j) => `${j === 0 ? 'M' : 'L'}${center(i)},${y(v)}`
                )
                .join(' ')}
              fill="none"
              stroke={DEMAND_COLORS.average}
              strokeWidth={2}
              strokeLinejoin="round"
            />
          )}
          {average.map(([i, v]) => (
            <circle
              key={i}
              cx={center(i)}
              cy={y(v)}
              r={2.5}
              fill={DEMAND_COLORS.average}
            />
          ))}
        </svg>
        {hover !== null && (
          <div
            className="absolute top-2 z-10 pointer-events-none bg-white border border-gray-200 shadow-md rounded-md px-3 py-2 text-sm min-w-[13rem]"
            style={
              center(hover) > width / 2
                ? { right: width - center(hover) + band / 2 }
                : { left: center(hover) + band / 2 }
            }
          >
            <div className="font-medium text-gray-900 mb-1">
              {label(monthly[hover].month)}
              {monthly[hover].partial && ` ${t('partDemand.chart.toDate')}`}
            </div>
            <TooltipRow
              color={DEMAND_COLORS.customer}
              value={qty(monthly[hover].customerDemand)}
              label={t('partDemand.chart.customer')}
            />
            <TooltipRow
              color={DEMAND_COLORS.internal}
              value={qty(monthly[hover].internalConsumption)}
              label={t('partDemand.chart.internal')}
            />
            {monthly[hover].returns > 0 && (
              <TooltipRow
                color={DEMAND_COLORS.returns}
                value={`−${qty(monthly[hover].returns)}`}
                label={t('partDemand.chart.returns')}
              />
            )}
            <TooltipRow
              value={qty(monthly[hover].totalDemand)}
              label={t('partDemand.chart.total')}
            />
            {monthly[hover].rollingAverage3M !== null && (
              <TooltipRow
                color={DEMAND_COLORS.average}
                value={qty(monthly[hover].rollingAverage3M as number)}
                label={t('partDemand.chart.average')}
              />
            )}
          </div>
        )}
      </div>
      {/* Hidden from view, for screen readers */}
      <div className="sr-only">
        <table>
          <caption>{t('partDemand.chart.title')}</caption>
          <thead>
            <tr>
              <th scope="col" />
              {[
                ...series.map((s) => s.label),
                t('partDemand.chart.total'),
                t('partDemand.chart.average'),
              ].map((header) => (
                <th key={header} scope="col">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {monthly.map((m) => (
              <tr key={m.month}>
                <th scope="row">{label(m.month)}</th>
                <td>{qty(m.customerDemand)}</td>
                <td>{qty(m.internalConsumption)}</td>
                <td>{qty(m.returns)}</td>
                <td>{qty(m.totalDemand)}</td>
                <td>
                  {m.rollingAverage3M === null ? '' : qty(m.rollingAverage3M)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
